import { Avatar, Box, Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";

import { makeProfileAvatarUrl } from "~utils/url";

interface ChatMessageProps {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  body: string;
  timestamp: string | Date;
  loggedInUserId?: string;
}

export const ChatMessage = ({ userName, userId, body, timestamp, loggedInUserId }: ChatMessageProps) => {
  const isOwnMessage = userId === loggedInUserId;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        sx={{
          "& > :not(:first-of-type)": {
            ml: isOwnMessage ? 0 : 2,
            mr: isOwnMessage ? 2 : 0,
          },
          flexDirection: isOwnMessage ? "row-reverse" : "row",
        }}
      >
        <Avatar src={userId ? makeProfileAvatarUrl(userId) : undefined} alt={userName} sx={{ height: 32, width: 32 }}>
          {userName[0]}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isOwnMessage ? "row-reverse" : "row",
              width: "100%",
            }}
          >
            {!isOwnMessage && (
              <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                {userName}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              "&::before": {
                borderColor: isOwnMessage
                  ? "transparent transparent transparent #90caf9"
                  : "transparent #f5f5f5 transparent transparent",
                borderStyle: "solid",
                borderWidth: "6px 12px 6px 0",
                content: '""',
                position: "absolute",
                [isOwnMessage ? "right" : "left"]: -6,
                top: 8,
                transform: isOwnMessage ? "scaleX(-1)" : "none",
              },
              position: "relative",
              textAlign: isOwnMessage ? "right" : "left",
            }}
          >
            <Box
              sx={{
                backgroundColor: isOwnMessage ? "primary.main" : "#f5f5f5",
                borderRadius: 2,
                display: "inline-block",
                maxWidth: "85%",
                minHeight: 26,
                minWidth: 52,
                p: "16px 16px 24px",
                position: "relative",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: isOwnMessage ? "primary.contrastText" : "text.primary",
                  mb: 2,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {body}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  bottom: 6,
                  color: isOwnMessage ? "primary.contrastText" : "text.secondary",
                  display: "block",
                  fontSize: "0.75rem",
                  opacity: 0.8,
                  position: "absolute",
                  right: 8,
                }}
              >
                {DateTime.fromISO(typeof timestamp === "string" ? timestamp : timestamp.toISOString()).toFormat(
                  "h:mm a",
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};
